import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { A } from './reviewUi';
import { sanitizeNoteHtml } from './noteHtml';

const TOOLS = [
  ['bold', <b key="b">B</b>, 'Bold'],
  ['italic', <i key="i">I</i>, 'Italic'],
  ['insertUnorderedList', '• List', 'Bullet list'],
  ['insertOrderedList', '1. List', 'Numbered list'],
];

/**
 * Small rich text box for notes: bold, italic, bullet and numbered lists.
 * Uncontrolled while typing; change `resetKey` to load `initialHtml` again (after adding or cancelling).
 */
const RichNoteEditor = ({ initialHtml = '', resetKey = 0, onChange, id, ariaLabel, placeholder, minHeight = 96, fill }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeNoteHtml(initialHtml);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    // a browser leaves a stray <br> after the text is deleted, which would hide the placeholder
    if (!el.textContent.trim() && !el.querySelector('li')) el.innerHTML = '';
    onChange?.(el.innerHTML);
  };

  const run = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd);
    emit();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...(fill && { flex: 1, minHeight: 0 }) }}>
      <Box role="toolbar" aria-label="Formatting" sx={{ display: 'flex', gap: 0.75 }}>
        {TOOLS.map(([cmd, label, title]) => (
          <Box
            key={cmd}
            component="button"
            type="button"
            title={title}
            aria-label={title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => run(cmd)}
            sx={{ border: `1px solid ${A.line}`, bgcolor: A.surface, color: A.ink, borderRadius: '8px', px: 1.25, py: 0.25, font: 'inherit', fontSize: 13, cursor: 'pointer', '&:hover': { bgcolor: A.soft }, '&:focus-visible': { outline: `2px solid ${A.sage}`, outlineOffset: 2 } }}
          >
            {label}
          </Box>
        ))}
      </Box>
      <Box
        ref={ref}
        id={id}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable
        suppressContentEditableWarning
        data-ph={placeholder}
        onInput={emit}
        onBlur={emit}
        sx={{
          minHeight, border: `1px solid ${A.line}`, bgcolor: A.surface, borderRadius: '10px', px: 1.5, py: 1.25, outline: 0, overflow: 'auto', color: A.ink, fontSize: 14, ...(fill && { flex: 1 }),
          '&:focus': { borderColor: A.btn },
          '&:empty::before': { content: 'attr(data-ph)', color: A.ink3 },
          '& ul, & ol': { m: '4px 0', pl: 2.75 }, '& p, & div': { m: 0 },
        }}
      />
    </Box>
  );
};

export default RichNoteEditor;
